import { Unstable_Popup as Popup, SelectProvider, useSelect } from '@mui/base';
import cn from 'classnames';
import { forwardRef, useMemo, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import useControlledValue from '../../../utils/hooks/use-controlled-value';
import { ChevronDownIcon, ChevronUpIcon } from '../../foundations/icon/icons';
import { Listbox } from '../../lists/listbox';
import { BaseInput } from '../base-input';
import { CheckboxField } from '../checkbox-field';

import { SelectContext } from './select-context';

import type { SelectValue } from '@mui/base';
import type { ForwardedRef, ReactElement } from 'react';
import type React from 'react';

import type { SelectComponentType, SelectProps } from './select.types';

import './select.scss';

const Select = forwardRef(
  <OptionValue extends NonNullable<unknown>, Multiple extends boolean = false>(
    props: SelectProps<OptionValue, Multiple>,
    forwardedRef: ForwardedRef<HTMLElement>,
  ): ReactElement => {
    const {
      children,
      className,
      defaultValue,
      disabled,
      icon,
      id,
      multiple,
      name,
      onChange,
      placeholder,
      readOnly,
      required,
      value: valueProp,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const [controlledValue, setControlledValue] = useControlledValue({
      controlledValue: valueProp,
      defaultValue,
      name: 'Select',
    });

    const handleChange = (
      event:
        | null
        | React.ChangeEvent<HTMLInputElement>
        | React.FocusEvent<Element, Element>
        | React.KeyboardEvent<Element>
        | React.MouseEvent<Element, MouseEvent>,
      value: SelectValue<OptionValue, Multiple>,
    ): void => {
      onChange?.(event, value);
      setControlledValue(value);
    };

    const triggerRef = useRef<HTMLDivElement>(null);

    const handleTriggerRef = mergeRefs([triggerRef, forwardedRef]);

    const contextProviderValue = useMemo(
      () => ({
        multiple,
      }),
      [multiple],
    );

    const {
      contextValue,
      getButtonProps,
      getHiddenInputProps,
      getListboxProps,
      getOptionMetadata,
      open,
      options,
      value: useSelectReturnedValue,
    } = useSelect<OptionValue, Multiple>({
      buttonRef: handleTriggerRef,
      disabled,
      multiple,
      name,
      onChange: handleChange,
      required,
      value: controlledValue,
    });

    const selectedValues = Array.isArray(useSelectReturnedValue) ? useSelectReturnedValue : [];
    const disabledSelectedOptions = selectedValues.filter((option) => getOptionMetadata(option)?.disabled);

    const enabledOptions = options.filter((option) => !getOptionMetadata(option)?.disabled);
    const enabledSelectedCount = selectedValues.filter((option) => enabledOptions.includes(option)).length;

    const allEnabledSelected = enabledSelectedCount === enabledOptions.length;
    const someEnabledSelected = enabledSelectedCount > 0 && enabledSelectedCount < enabledOptions.length;

    const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      const newSelectedValues = event.target.checked
        ? [...disabledSelectedOptions, ...enabledOptions]
        : disabledSelectedOptions;
      handleChange(event, newSelectedValues as SelectValue<OptionValue, Multiple>);
    };

    const renderSelectedValue = (selectedValue: SelectValue<OptionValue, Multiple> | undefined): null | string => {
      if (!selectedValue) {
        return null;
      }

      if (Array.isArray(selectedValue)) {
        return selectedValue.map((v) => getOptionMetadata(v)?.label ?? '').join(', ');
      }

      const label = getOptionMetadata(selectedValue as OptionValue)?.label;

      return typeof label === 'string' ? label : null;
    };

    const triggerProps = disabled || readOnly ? {} : getButtonProps();
    const triggerClasses = cn(
      'rbui-select-trigger',
      {
        'rbui-select-trigger--disabled': disabled,
        'rbui-select-trigger--readonly': readOnly,
      },
      className,
    );

    const Chevron = open ? ChevronUpIcon : ChevronDownIcon;

    return (
      <>
        <BaseInput
          {...triggerProps}
          className={triggerClasses}
          disabled={disabled}
          id={id}
          inputProps={{
            onMouseDown: (event) => event.preventDefault(), // Allow close the popup when clicking the input field
            readOnly: true, // Prevent internal input from entering into the edit mode
          }}
          leftElement={icon}
          placeholder={placeholder}
          readOnly={readOnly}
          renderInput={(inputProps, inputRef) => {
            return (
              <>
                <input {...inputProps} ref={inputRef} tabIndex={-1} />
                <input {...getHiddenInputProps()} />
              </>
            );
          }}
          rightElement={<Chevron aria-hidden className="rbui-select-chevron" />}
          value={renderSelectedValue(useSelectReturnedValue) ?? ''}
          {...rest}
        />
        <Popup
          anchor={triggerRef.current}
          keepMounted
          offset={0}
          open={open}
          placement="bottom-start"
          role={undefined}
          style={{
            display: open ? 'block' : 'none',
            minWidth: triggerRef.current?.offsetWidth,
          }}
        >
          <Listbox className="rbui-select-menu" {...getListboxProps()}>
            <SelectProvider value={contextValue}>
              <SelectContext.Provider value={contextProviderValue}>
                {multiple && options.length > 0 && (
                  <>
                    <li className="rbui-select-menu-header">
                      <CheckboxField
                        checked={allEnabledSelected}
                        indeterminate={someEnabledSelected}
                        label="Select all"
                        onChange={handleSelectAllChange}
                      />
                      <span className="rbui-selected-count">({selectedValues.length} selected)</span>
                    </li>
                    <li className="rbui-select-menu-divider" />
                  </>
                )}
                {children}
              </SelectContext.Provider>
            </SelectProvider>
          </Listbox>
        </Popup>
      </>
    );
  },
);

Select.displayName = 'Select';

/**
 * @internal
 *
 * The `Select` component is meant to optimize user interaction and experience.
 * It is a customizable dropdown list that allows users to choose one or more options from a list.
 * Its primary function is to enhance and simplify data input, minimizing user effort
 * in navigation while avoiding incorrect entries.
 *
 * ### Usage
 *
 * ```tsx
 * import { Select, SelectOption } from '@bloomreach/react-banana-ui';
 * import React from 'react';
 *
 * export default function MyCustomSingleSelect() {
 *   return (
 *     <Select
 *       label="List of fruits"
 *       placeholder="Select a fruit"
 *       onChange={(event, value) => console.log(value)}
 *     >
 *       <SelectOption value="apple">Apple</SelectOption>
 *       <SelectOption value="banana">Banana</SelectOption>
 *       <SelectOption value="orange">Orange</SelectOption>
 *       <SelectOption value="grapes">Grapes</SelectOption>
 *       <SelectOption value="strawberries">Strawberries</SelectOption>
 *       <SelectOption value="blueberries">Blueberries</SelectOption>
 *       <SelectOption value="pineapple">Pineapple</SelectOption>
 *     </Select>
 *   );
 * }
 * ```
 */
export default Select as SelectComponentType;
